import { useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import { infoMappingFlow, traceMappingFlow, warnMappingFlow } from '../lib/mappingTrace';
import { SlowMappingGateLogDelayMs } from '../lib/mappingPageHostBridge';
import type { ActiveDocumentInfo } from '../../../shared/hooks/useActiveDocumentName';
import type { useMappingProjectReadiness } from './useMappingProjectReadiness';
import type { useProjectProfileGate } from './useProjectProfileGate';

/**
 * Observability sink for the mapping workspace view model. All trace/stall
 * effects live here so the content-model hook stays about view state, not log
 * emission. Every effect mirrors the payload + dependency list that used to
 * sit inline in useMappingWorkspaceContentModel — inputs are grouped, but each
 * effect still depends on the same primitive fields as before.
 */

type MappingReadiness = ReturnType<typeof useMappingProjectReadiness>;
type ProjectProfileGate = ReturnType<typeof useProjectProfileGate>;

export interface MappingWorkspaceDiagnosticsInput {
  processConfig: {
    processConditionsChecked: boolean;
    projectPropertiesChecked: boolean;
    processConditionsValue?: { value?: string } | null;
    projectPropertiesValue?: { value?: string } | null;
  };
  activeDocumentInfo: Pick<ActiveDocumentInfo, 'documentName' | 'projectKey' | 'modelVersion'>;
  gate: {
    activeProjectSessionKey: ProjectProfileGate['activeProjectSessionKey'];
    resolvedProfileKey: ProjectProfileGate['resolvedProfileKey'];
    resolvedSettingsType: ProjectProfileGate['resolvedSettingsType'];
    resumeProfileKey: ProjectProfileGate['resumeProfileKey'];
    resumeScopeKey: ProjectProfileGate['resumeScopeKey'];
    profileConfirmedForCurrentSession: ProjectProfileGate['profileConfirmedForCurrentSession'];
    projectSessionConfirmedProfileKey: ProjectProfileGate['projectSessionConfirmedProfileKey'];
    projectSessionProfileConfirmationSource: ProjectProfileGate['projectSessionProfileConfirmationSource'];
    restorePersistedWorkspace: ProjectProfileGate['restorePersistedWorkspace'];
    projectProfileTrustSource: string | null;
    projectProfileGateDecision: ProjectProfileGate['projectProfileGateDecision'];
    projectProfileGateEvaluationNeeded: ProjectProfileGate['projectProfileGateEvaluationNeeded'];
    projectProfileGateOpen: ProjectProfileGate['projectProfileGateOpen'];
    projectProfileAutoPassProfileKey: ProjectProfileGate['projectProfileAutoPassProfileKey'];
    projectProfileAutoPassReason: ProjectProfileGate['projectProfileAutoPassReason'];
    projectProfileAutoPassBlocked: ProjectProfileGate['projectProfileAutoPassBlocked'];
    projectProfileAutoPassLoadedKey: ProjectProfileGate['projectProfileAutoPassLoadedKey'];
    manualProjectProfileSelectionActive: ProjectProfileGate['manualProjectProfileSelectionActive'];
    quickFillProfileRestorePending: ProjectProfileGate['quickFillProfileRestorePending'];
    profileProjectMatches: ProjectProfileGate['profileProjectMatches'];
  };
  readiness: MappingReadiness;
  flags: {
    mappingReadinessEnabled: boolean;
    dockablePaneVisible: boolean;
    profileAutoRepairAllowed: boolean;
    profileAutoRepairBlockedMessage: string | null;
    isLiveWizardMappingHandoff: boolean;
    hasResolvedSelectedProfile: boolean;
    confirmedRepairProfileKey: string | null;
    resolvedProfileName: string;
    resolvedProfileMissingPreview: string | null;
    profilesLoading: boolean;
    lastAppliedProfileKey: string;
    selectedProfileKey: string;
    projectIdentityPending: boolean;
    projectPreferredProfileKey: string | null;
    activeTab: number;
  };
  applyAttention: {
    activeTab: number;
    isQuickFillWorkspaceTab: boolean;
    workspaceApplyDisabled: boolean;
    workspaceContinueDisabled: boolean;
    workspaceApplyNeedsAttention: boolean;
    quickFillHasPendingChanges: boolean;
    quickFillCanApplyWhileMappingBlocked: boolean;
    currentSettingsNeedProfileSave: boolean;
    isSaving: boolean;
    mappingReadinessBlocksWorkspaceApply: boolean;
    projectProfileGateBlocksActions: boolean;
    workspaceApplyAttentionTraceKey: MutableRefObject<string | null>;
  };
}

export function useMappingWorkspaceDiagnostics({
  processConfig,
  activeDocumentInfo,
  gate,
  readiness,
  flags,
  applyAttention,
}: MappingWorkspaceDiagnosticsInput): void {
  useEffect(() => {
    traceMappingFlow('page scope resolved process config', {
      processConditionsChecked: processConfig.processConditionsChecked,
      projectPropertiesChecked: processConfig.projectPropertiesChecked,
      processConditionsValue: processConfig.processConditionsValue?.value,
      projectPropertiesValue: processConfig.projectPropertiesValue?.value,
    });
  }, [
    processConfig.processConditionsChecked,
    processConfig.projectPropertiesChecked,
    processConfig.processConditionsValue?.value,
    processConfig.projectPropertiesValue?.value,
  ]);

  // The readiness arrays arrive as fresh references on almost every render,
  // so the effect refires constantly; without a payload signature the session
  // log fills with dozens of identical dumps per second.
  const readinessAlertSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    const payload = {
      resolvedProfileKey: gate.resolvedProfileKey,
      resolvedProfileName: flags.resolvedProfileName,
      profileConfirmedForCurrentSession: gate.profileConfirmedForCurrentSession,
      confirmedRepairProfileKey: flags.confirmedRepairProfileKey,
      projectProfileTrustSource: gate.projectProfileTrustSource,
      profileAutoRepairAllowed: flags.profileAutoRepairAllowed,
      autoRepairBlockedMessage: flags.profileAutoRepairBlockedMessage,
      resolvedProfileMissingPreview: flags.resolvedProfileMissingPreview,
      mappingReadinessStatus: readiness.status,
      mappingReadinessBlocking: readiness.isBlocking,
      mappingReadinessBlockReason: readiness.blockReason,
      mappingReadinessMessage: readiness.message,
      missingParameterNames: readiness.missingParameterNames,
      repairableParameterNames: readiness.repairableParameterNames,
      blockedParameterNames: readiness.blockedParameterNames,
      hasAttemptedRepair: readiness.hasAttemptedRepair,
      isRepairing: readiness.isRepairing,
      repairErrorMessage: readiness.repairErrorMessage,
    };
    const signature = JSON.stringify(payload);
    if (signature === readinessAlertSignatureRef.current) {
      return;
    }
    readinessAlertSignatureRef.current = signature;

    infoMappingFlow('mapping readiness alert context', payload);
  }, [
    flags.confirmedRepairProfileKey,
    readiness.blockReason,
    readiness.blockedParameterNames,
    readiness.hasAttemptedRepair,
    readiness.isBlocking,
    readiness.isRepairing,
    readiness.message,
    readiness.missingParameterNames,
    readiness.repairErrorMessage,
    readiness.repairableParameterNames,
    readiness.status,
    flags.profileAutoRepairAllowed,
    flags.profileAutoRepairBlockedMessage,
    gate.profileConfirmedForCurrentSession,
    gate.projectProfileTrustSource,
    gate.resolvedProfileKey,
    flags.resolvedProfileMissingPreview,
    flags.resolvedProfileName,
  ]);

  const readinessMissingParametersKey = readiness.missingParameterNames.join('|');
  const readinessBlockedParametersKey = readiness.blockedParameterNames.join('|');
  const readinessRepairableParametersKey = readiness.repairableParameterNames.join('|');

  useEffect(() => {
    traceMappingFlow('page scope resolved', {
      documentName: activeDocumentInfo.documentName,
      projectKey: activeDocumentInfo.projectKey,
      modelVersion: activeDocumentInfo.modelVersion,
      resumeScopeKey: gate.resumeScopeKey,
      projectIdentityPending: flags.projectIdentityPending,
      profileKey: gate.resolvedProfileKey,
      settingsType: gate.resolvedSettingsType,
      resumeProfileKey: gate.resumeProfileKey,
      mappingReadinessEnabled: flags.mappingReadinessEnabled,
      dockablePaneVisible: flags.dockablePaneVisible,
      profileAutoRepairAllowed: flags.profileAutoRepairAllowed,
      isLiveWizardMappingHandoff: flags.isLiveWizardMappingHandoff,
      hasResolvedSelectedProfile: flags.hasResolvedSelectedProfile,
      profileConfirmedForCurrentSession: gate.profileConfirmedForCurrentSession,
      projectSessionConfirmedProfileKey: gate.projectSessionConfirmedProfileKey,
      projectSessionProfileConfirmationSource: gate.projectSessionProfileConfirmationSource,
      restorePersistedWorkspace: gate.restorePersistedWorkspace,
      profilesLoading: flags.profilesLoading,
      profileMatchesLoading: gate.profileProjectMatches.isLoading,
      profileMatchCatalogReady: gate.profileProjectMatches.catalogReady,
      topMatchProfileKey: gate.profileProjectMatches.topMatchProfileKey,
      exactProfileMatchCount: gate.profileProjectMatches.exactMatchCount,
      profileMatchCatalogSource: gate.profileProjectMatches.catalogSource,
      profileMatchCatalogError: gate.profileProjectMatches.catalogError,
      profileTrustSource: gate.projectProfileTrustSource,
      projectPreferredProfileKey: flags.projectPreferredProfileKey,
      projectProfileGateDecision: gate.projectProfileGateDecision,
      projectProfileGateEvaluationNeeded: gate.projectProfileGateEvaluationNeeded,
      projectProfileGateOpen: gate.projectProfileGateOpen,
      projectProfileAutoPassProfileKey: gate.projectProfileAutoPassProfileKey,
      projectProfileAutoPassReason: gate.projectProfileAutoPassReason,
      projectProfileAutoPassBlocked: gate.projectProfileAutoPassBlocked,
      projectProfileAutoPassLoadedKey: gate.projectProfileAutoPassLoadedKey,
      manualProjectProfileSelectionActive: gate.manualProjectProfileSelectionActive,
      quickFillProfileRestorePending: gate.quickFillProfileRestorePending,
      lastAppliedProfileKey: flags.lastAppliedProfileKey,
      readinessStatus: readiness.status,
      readinessBlocking: readiness.isBlocking,
      readinessBlockReason: readiness.blockReason,
      missingParameters: readinessMissingParametersKey
        ? readinessMissingParametersKey.split('|')
        : [],
      blockedParameters: readinessBlockedParametersKey
        ? readinessBlockedParametersKey.split('|')
        : [],
      repairableParameters: readinessRepairableParametersKey
        ? readinessRepairableParametersKey.split('|')
        : [],
    });
  }, [
    activeDocumentInfo.documentName,
    activeDocumentInfo.modelVersion,
    activeDocumentInfo.projectKey,
    flags.dockablePaneVisible,
    flags.hasResolvedSelectedProfile,
    flags.isLiveWizardMappingHandoff,
    readiness.isBlocking,
    readiness.blockReason,
    readiness.status,
    flags.mappingReadinessEnabled,
    flags.profileAutoRepairAllowed,
    gate.profileConfirmedForCurrentSession,
    flags.projectIdentityPending,
    gate.profileProjectMatches.exactMatchCount,
    gate.profileProjectMatches.catalogReady,
    gate.profileProjectMatches.catalogError,
    gate.profileProjectMatches.catalogSource,
    gate.profileProjectMatches.isLoading,
    gate.profileProjectMatches.topMatchProfileKey,
    flags.profilesLoading,
    flags.projectPreferredProfileKey,
    gate.projectProfileAutoPassBlocked,
    gate.projectProfileAutoPassLoadedKey,
    gate.projectProfileAutoPassProfileKey,
    gate.manualProjectProfileSelectionActive,
    flags.lastAppliedProfileKey,
    gate.projectProfileAutoPassReason,
    gate.projectProfileGateDecision,
    gate.projectProfileGateEvaluationNeeded,
    gate.projectProfileGateOpen,
    gate.quickFillProfileRestorePending,
    gate.projectProfileTrustSource,
    gate.projectSessionConfirmedProfileKey,
    gate.projectSessionProfileConfirmationSource,
    gate.restorePersistedWorkspace,
    readinessBlockedParametersKey,
    readinessMissingParametersKey,
    readinessRepairableParametersKey,
    gate.resolvedProfileKey,
    gate.resolvedSettingsType,
    gate.resumeProfileKey,
    gate.resumeScopeKey,
  ]);

  useEffect(() => {
    if (!gate.projectProfileGateEvaluationNeeded || gate.projectProfileGateDecision.mode !== 'checking') {
      return;
    }

    const timeoutId = globalThis.setTimeout(() => {
      warnMappingFlow('project profile gate still checking', {
        documentName: activeDocumentInfo.documentName,
        projectKey: activeDocumentInfo.projectKey,
        modelVersion: activeDocumentInfo.modelVersion,
        activeProjectSessionKey: gate.activeProjectSessionKey,
        profileKey: gate.resolvedProfileKey,
        profilesLoading: flags.profilesLoading,
        profileMatchesLoading: gate.profileProjectMatches.isLoading,
        profileMatchCatalogReady: gate.profileProjectMatches.catalogReady,
        profileMatchCatalogSource: gate.profileProjectMatches.catalogSource,
        profileMatchCatalogError: gate.profileProjectMatches.catalogError,
        projectProfileGateDecision: gate.projectProfileGateDecision,
        slowLogDelayMs: SlowMappingGateLogDelayMs,
      });
    }, SlowMappingGateLogDelayMs);

    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [
    activeDocumentInfo.documentName,
    activeDocumentInfo.modelVersion,
    activeDocumentInfo.projectKey,
    gate.activeProjectSessionKey,
    gate.profileProjectMatches.catalogError,
    gate.profileProjectMatches.catalogReady,
    gate.profileProjectMatches.catalogSource,
    gate.profileProjectMatches.isLoading,
    flags.profilesLoading,
    gate.projectProfileGateDecision,
    gate.projectProfileGateEvaluationNeeded,
    gate.resolvedProfileKey,
  ]);

  useEffect(() => {
    if (readiness.status !== 'checking') {
      return;
    }

    const timeoutId = globalThis.setTimeout(() => {
      warnMappingFlow('mapping readiness still checking', {
        documentName: activeDocumentInfo.documentName,
        projectKey: activeDocumentInfo.projectKey,
        modelVersion: activeDocumentInfo.modelVersion,
        activeProjectSessionKey: gate.activeProjectSessionKey,
        profileKey: gate.resolvedProfileKey,
        mappingReadinessEnabled: flags.mappingReadinessEnabled,
        profileConfirmedForCurrentSession: gate.profileConfirmedForCurrentSession,
        profileTrustSource: gate.projectProfileTrustSource,
        blockReason: readiness.blockReason,
        slowLogDelayMs: SlowMappingGateLogDelayMs,
      });
    }, SlowMappingGateLogDelayMs);

    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [
    activeDocumentInfo.documentName,
    activeDocumentInfo.modelVersion,
    activeDocumentInfo.projectKey,
    gate.activeProjectSessionKey,
    readiness.blockReason,
    readiness.status,
    flags.mappingReadinessEnabled,
    gate.profileConfirmedForCurrentSession,
    gate.projectProfileTrustSource,
    gate.resolvedProfileKey,
  ]);

  useEffect(() => {
    if (flags.activeTab !== 1 || !gate.quickFillProfileRestorePending) {
      return;
    }

    traceMappingFlow('quick fill deferred until project profile restore', {
      documentName: activeDocumentInfo.documentName,
      projectKey: activeDocumentInfo.projectKey,
      modelVersion: activeDocumentInfo.modelVersion,
      selectedProfileKey: flags.selectedProfileKey,
      projectPreferredProfileKey: flags.projectPreferredProfileKey,
      autoPassProfileKey: gate.projectProfileAutoPassProfileKey,
      gateOpen: gate.projectProfileGateOpen,
    });
  }, [
    activeDocumentInfo.documentName,
    activeDocumentInfo.modelVersion,
    activeDocumentInfo.projectKey,
    flags.activeTab,
    flags.projectPreferredProfileKey,
    gate.projectProfileAutoPassProfileKey,
    gate.projectProfileGateOpen,
    gate.quickFillProfileRestorePending,
    flags.selectedProfileKey,
  ]);

  useEffect(() => {
    const payload = {
      activeTab: applyAttention.activeTab,
      isQuickFillWorkspaceTab: applyAttention.isQuickFillWorkspaceTab,
      workspaceApplyDisabled: applyAttention.workspaceApplyDisabled,
      workspaceContinueDisabled: applyAttention.workspaceContinueDisabled,
      workspaceApplyNeedsAttention: applyAttention.workspaceApplyNeedsAttention,
      quickFillHasPendingChanges: applyAttention.quickFillHasPendingChanges,
      quickFillCanApplyWhileMappingBlocked: applyAttention.quickFillCanApplyWhileMappingBlocked,
      currentSettingsNeedProfileSave: applyAttention.currentSettingsNeedProfileSave,
      isSaving: applyAttention.isSaving,
      projectIdentityPending: flags.projectIdentityPending,
      mappingReadinessBlocking: readiness.isBlocking,
      mappingReadinessBlocksWorkspaceApply: applyAttention.mappingReadinessBlocksWorkspaceApply,
      projectProfileGateBlocksActions: applyAttention.projectProfileGateBlocksActions,
    };
    const traceKey = JSON.stringify(payload);
    if (applyAttention.workspaceApplyAttentionTraceKey.current === traceKey) {
      return;
    }

    applyAttention.workspaceApplyAttentionTraceKey.current = traceKey;
    infoMappingFlow('workspace apply attention state', payload);
  }, [
    applyAttention.activeTab,
    applyAttention.currentSettingsNeedProfileSave,
    applyAttention.workspaceApplyAttentionTraceKey,
    applyAttention.isSaving,
    applyAttention.isQuickFillWorkspaceTab,
    readiness.isBlocking,
    applyAttention.mappingReadinessBlocksWorkspaceApply,
    flags.projectIdentityPending,
    applyAttention.projectProfileGateBlocksActions,
    applyAttention.quickFillCanApplyWhileMappingBlocked,
    applyAttention.quickFillHasPendingChanges,
    applyAttention.workspaceApplyDisabled,
    applyAttention.workspaceApplyNeedsAttention,
    applyAttention.workspaceContinueDisabled,
  ]);
}
